import PropTypes from 'prop-types';
import { useTheme } from 'styled-components';
import InputTextLine from '../../../juristec-ui/core/InputTextLine';
import {
  Add, ArrowLeft, DisplayGrid, DisplayList, Finder,
  Report,
} from '../../../juristec-ui/icons';
import IconButton from '../../../juristec-ui/core/IconButton';
import { printSize } from '../../../juristec-ui/utils/functions/lab';
import Tooltip from '../../../juristec-ui/core/Tooltip';

import {
  MainContainer,
  ProgressContainer,
  ProgressBar,
  SearchBarContainer,
  IconWrapper,
  ItensContainer,
  BadgeCount,
} from './styled/FilesToolbar.styled';

const filesToolbarDefaultPropTourContext = { tourOpen: false };

/**
* A component that has file management options
*/
const FilesToolbar = ({
  sizeUsed = 0,
  sizeLimit = 0,
  textSearch = '',
  setTextSearch,
  viewLayout,
  toggleViewLayout,
  uploadFile,
  generateReport,
  isMobileSize = true,
  tourContext = filesToolbarDefaultPropTourContext,
  showExtracted = false,
  toggleExtracted,
  extractedReportsCount = 0,
}) => {
  const theme = useTheme();

  return (
    <MainContainer>
      <ItensContainer className="left">
        <ProgressContainer>
          <ProgressBar position={(sizeUsed * 100) / sizeLimit} />
        </ProgressContainer>
        <span>
          {printSize(sizeUsed).replace('.', ',')}
          {' '}
          /
          {' '}
          {printSize(sizeLimit).replace('.', ',')}
        </span>
      </ItensContainer>
      <ItensContainer className="right">
        <SearchBarContainer>
          <IconWrapper>
            <Finder />
          </IconWrapper>
          <InputTextLine
            placeholder={showExtracted ? 'Buscar relatórios extraídos...' : 'Buscar arquivos...'}
            value={textSearch}
            autoComplete="off"
            onChange={(e) => setTextSearch(e.target.value)}
            style={{ color: theme.color }}
          />
        </SearchBarContainer>
        {!isMobileSize && (
          <>
            <Tooltip text="Relatórios extraídos">
              <IconButton
                onClick={toggleExtracted}
                shape="rounded"
                color="primary"
                style={{ position: 'relative' }}
              >
                {showExtracted ? <ArrowLeft /> : <DisplayList />}
                {extractedReportsCount > 0 && (
                  <BadgeCount>{extractedReportsCount}</BadgeCount>
                )}
              </IconButton>
            </Tooltip>
            <Tooltip text="Gerar relatório">
              <IconButton
                onClick={generateReport}
                shape="rounded"
                color="primary"
                variant="pattern"
                disabled={showExtracted}
              >
                <Report />
              </IconButton>
            </Tooltip>
            <Tooltip text={viewLayout ? 'Grade de arquivos' : 'Lista de arquivos'}>
              <IconButton
                onClick={toggleViewLayout}
                shape="rounded"
                color="primary"
                variant={isMobileSize ? 'contained' : 'pattern'}
                disabled={showExtracted}
              >
                {viewLayout ? <DisplayGrid /> : <DisplayList />}
              </IconButton>
            </Tooltip>
            <Tooltip text="Upload de arquivos e tabelas">
              <IconButton
                onClick={() => { uploadFile(); if (tourContext.tourOpen) tourContext.nextStep(); }}
                shape="rounded"
                color="secondary"
                variant="contained"
                disabled={sizeUsed > sizeLimit || showExtracted}
                className="upload_file_btn"
              >
                <Add />
              </IconButton>
            </Tooltip>
          </>
        )}
      </ItensContainer>
    </MainContainer>
  );
};

FilesToolbar.propTypes = {
  /**
    * The size in Bytes used by the uploaded files
    */
  sizeUsed: PropTypes.number,
  /**
    * The size limit in Bytes for files upload
    */
  sizeLimit: PropTypes.number,
  /**
    * String input of the search bar
    */
  textSearch: PropTypes.string,
  /**
    * Function that deals with the changes on the search bar
    */
  setTextSearch: PropTypes.func.isRequired,
  /**
    * A boolean state that indicates what layout has been used (true for grid, false for list)
    */
  viewLayout: PropTypes.bool.isRequired,
  /**
    * A function that toggles the layout types
    */
  toggleViewLayout: PropTypes.func.isRequired,
  /**
    * Function to add a new file
    */
  uploadFile: PropTypes.func.isRequired,
  /**
    * Function to generate a new report
    */
  generateReport: PropTypes.func.isRequired,
  /**
   * A boolean state that indicates the user device based on screen size
   */
  isMobileSize: PropTypes.bool,
  /**
   * A boolean state that indicates if extracted reports are shown
   */
  showExtracted: PropTypes.bool,
  /**
   * A function to toggle the extracted reports view
   */
  toggleExtracted: PropTypes.func.isRequired,
  /**
   * A context object that contains the tour state
  */
  tourContext: PropTypes.shape({
    tourOpen: PropTypes.bool,
  }),
  /**
   * The count of extracted reports
   */
  extractedReportsCount: PropTypes.number,
};

export default FilesToolbar;
